import type {
  ContratCxpiDossierDTO,
  ContratCxpiDTO,
  ContratDTO,
  EmployeurDTO,
  FormationDTO,
  MissionDTO,
  ModuleDTO,
  SalarieDTO,
} from '@/features/api/extranet/dto/Demande/CXPI'
import type { DureeHebdomadaireDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { FinancementDTO } from '@/features/api/extranet/dto/Demande/shared/FinancementDTO'
import type { AdresseDTO } from '@/features/api/extranet/dto/shared'
import { CODE_POSTAL_MAYOTTE } from '@/features/domaine/constantes'
import {
  addPreBuilder,
  type Flag,
  type ModelValidateurResult,
  type Regles,
  targetPreBuilder,
} from '@/features/domaine/moteur-regles'
import { PropertyStatus } from '@/features/domaine/property-status'
import { getNaturesFichiersCxpi } from '@/features/domaine/regles-akto/CXPI/naturesFichiers.ts'
import {
  checkNir,
  checkNirDateNaissance,
  checkNirSexe,
  checkNirTemporaire,
  getRangeDate,
  getSmicHoraireBrut,
  preDateValide,
  preEstNirPortail,
  preNombreValide,
  preNotNullOrEmpty,
  preStatusBuilder,
  rgEstCodePostalFactory,
  rgEstCommuneFactory,
  rgEstDate,
  rgEstMailFactory,
  rgEstMaxLengthFactory,
  rgEstNomOuPrenomFactory,
  rgEstNumeric,
  rgEstNumericMaxFactory,
  rgEstNumericMinFactory,
  rgEstNumericMinOrEqualFactory,
  rgEstNumTelFactory,
  rgEstObligatoireFactory,
  rgEstVoieFactory,
  rgEstVraiFactory,
} from '@/features/domaine/regles-akto/shared'
import { SAISIE_INVALIDE, SAISIE_OBLIGATOIRE } from '@/features/domaine/regles-akto/shared/messages-erreurs.ts'
import { LevelRegleEnum } from '@/features/domaine/types/opco'
import {
  addMonth,
  getNbJour,
  isBoolean,
  isDate,
  isNotNullOrUndefined,
  isNullOrUndefined,
  isNullUndefinedOrEmptyString,
  isNumber,
} from '@/features/shared/tools'
import { addDays, addWorkingDays, checkDateInRange, getAge, getToday } from '@/features/shared/tools/date.ts'
import { isNotNullUndefinedOrEmptyString } from '@/features/shared/tools/object.ts'
import { preEstCDPI, preEstCIPI, preNirTemporaire, preNotNirTemporaire } from './preconditions.ts'
import type { ContextReglesCxpi, ReglesCxpi } from './types.ts'
import type {
  Statut as StatutReservationFonds
} from '@/features/domaine/types/opco/Reservations-fonds/Dossier/Statut.ts'

/* Initalisation des helpers */
const targetPre = targetPreBuilder<ContratCxpiDTO, ContextReglesCxpi>()
const addPre = addPreBuilder<ContratCxpiDTO, ContextReglesCxpi>()

type RegleCXPIMembre<T extends object> = Regles<T, ContextReglesCxpi, LevelRegleEnum, Flag, ContratCxpiDTO>

type ReglesEmployeur = RegleCXPIMembre<EmployeurDTO>
type ReglesSalarie = RegleCXPIMembre<SalarieDTO>
type ReglesContrat = RegleCXPIMembre<ContratDTO>
type ReglesFormation = RegleCXPIMembre<FormationDTO>
type ReglesMission = RegleCXPIMembre<MissionDTO>
type ReglesFinancement = RegleCXPIMembre<FinancementDTO>
type ReglesDrf = RegleCXPIMembre<ContratCxpiDossierDTO>

/* Tools */
function estDureeHebdomadaireDTO(model: any): model is DureeHebdomadaireDTO {
  return (
    typeof model === 'object' &&
    model !== null &&
    typeof model.heure === 'number' &&
    typeof model.minute === 'number' &&
    model.heure >= 0 &&
    model.minute >= 0 &&
    model.minute < 60
  )
}

function preEstDossier(_value: any, _parent: any, model: ContratCxpiDTO) {
  return model.typeDemande === 'Formation'
}

function preEstReservation(_value: any, _parent: any, model: ContratCxpiDTO) {
  return model.typeDemande === 'ReservationFonds'
}
/*
function isDerogationLancement(isDossier: boolean): boolean {
  const today = getToday()
  const dateDebut = new Date('2026-01-02')
  const dateFin = isDossier ? new Date('2026-02-16') : new Date('2026-01-16')

  return today >= dateDebut && today <= dateFin
}

function isDerogationLancementDate(dateDebut: Date): boolean {
  const dateDebutMin = new Date('2025-12-17')
  const dateFinMax = new Date('2026-01-05')
  return dateDebut >= dateDebutMin && dateDebut <= dateFinMax
}
*/
function isDerogationDepotDate(dateDepot: Date): boolean {
  const dateDebutMin = new Date('2025-11-15')
  const dateFinMax = new Date('2026-02-02')
  return dateDepot >= dateDebutMin && dateDepot <= dateFinMax
}

const preEstPresent = preStatusBuilder<ContratCxpiDTO, ContextReglesCxpi>(
  PropertyStatus.Optional,
  PropertyStatus.Required,
)
const preEstObligatoire = preStatusBuilder<ContratCxpiDTO, ContextReglesCxpi>(PropertyStatus.Required)

function periodeFormationExternes(model: ContratCxpiDTO): { dateDebut?: string; dateFin?: string } {
  if (!model.formation?.modules) return {}
  return model.formation.modules
    .filter((module) => module.typeModule === 'Externe')
    .reduce(
      (periodeFormation, module) => {
        if (!module.dateDebut && !module.dateFin) return periodeFormation

        if (isDate(module.dateDebut)) {
          if (!periodeFormation.dateDebut || module.dateDebut < periodeFormation.dateDebut) {
            periodeFormation.dateDebut = module.dateDebut
          }
        }

        if (isDate(module.dateFin)) {
          if (!periodeFormation.dateFin || module.dateFin > periodeFormation.dateFin) {
            periodeFormation.dateFin = module.dateFin
          }
        }

        return periodeFormation
      },
      {} as ReturnType<typeof periodeFormationExternes>,
    )
}

function getNbHeureModulesExternes(model: ContratCxpiDTO) {
  if (!model.formation?.modules) return 0
  return model.formation?.modules
    ?.filter((m) => m.typeModule === 'Externe')
    ?.filter((m) => isNumber(m.duree))
    ?.reduce((somme, module) => (somme += module.duree!), 0)
}

function getNbHeureModulesAutres(model: ContratCxpiDTO) {
  if (!model.formation?.modules) return 0
  return model.formation?.modules
    ?.filter((m) => m.typeModule !== 'Externe')
    ?.filter((m) => isNumber(m.duree))
    ?.reduce((somme, module) => (somme += module.duree!), 0)
}

/* REGLES  Employeur ------------------------------------------------------------ */
const reglesEmployeur: ReglesEmployeur = {
  contact: {
    telephone: {
      ...rgEstMaxLengthFactory(10),
      ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    },
    email: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.email?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.email?.$status !== PropertyStatus.NotPresent,
        rgEstMailFactory(LevelRegleEnum.Metier),
      ),
    },
  },
  effectifTotalEntreprise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.effectifTotalEntreprise?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstNumeric,
    ...rgEstNumericMinFactory(0, LevelRegleEnum.Metier),
  },
}

/* REGLES  Salarié  ------------------------------------------------------------ */
const reglesSalarie: ReglesSalarie = {
  nomNaissance: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  prenom: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  sexe: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    controleSexeNir: {
      pre: [preNotNullOrEmpty, targetPre('salarie.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirSexe(parent?.nir ?? '', value!),
      message: 'Le sexe du salarié ne correspond pas au NIR saisi',
      level: LevelRegleEnum.Metier,
    },
  },
  dateNaissance: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstDate,
    controleDateNaissanceNir: {
      pre: [preNotNullOrEmpty, targetPre('salarie.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirDateNaissance((parent as SalarieDTO)?.nir ?? '', value!),
      message: 'La date de naissance du salarié ne correspond pas au NIR saisi',
      level: LevelRegleEnum.Metier,
    },
  },
  nirTemporaire: rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  nir: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstMaxLengthFactory(40),
    ...addPre(preNotNirTemporaire, rgEstMaxLengthFactory(13, LevelRegleEnum.Transmettre)),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    'check-nir': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNotNirTemporaire],
      validateur: (value) => checkNir(value!),
      message: 'Le format du NIR est incorrect',
      level: LevelRegleEnum.Metier,
    },
    'check-nir-temporaire': {
      pre: [preNotNullOrEmpty, preNirTemporaire, preEstNirPortail],
      validateur: (value) => checkNirTemporaire(value!),
      message: 'Le format du NIR temporaire est incorrect',
      level: LevelRegleEnum.Metier,
    },
  },
  beneficieReconnaissanceTravailleurHandicape: rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  csp: rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  contratPacea: {
    ...addPre(preEstObligatoire('salarie.contratPacea'), rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
  },
  contratEngagementJeune: {
    ...addPre(preEstObligatoire('salarie.contratEngagementJeune'), rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
  },
  inscritFranceTravail: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  justifieAncienneteInterim: {
    ...addPre(
      preEstObligatoire('salarie.justifieAncienneteInterim'),
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  nombreHeuresAncienneteInterim: {
    ...addPre(
      preEstObligatoire('salarie.nombreHeuresAncienneteInterim'),
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...addPre(preEstPresent('salarie.nombreHeuresAncienneteInterim'), {
      ...rgEstNumeric,
      ...rgEstNumericMinOrEqualFactory(150),
    }),
  },
  categoriePublic: {
    ...addPre(preEstObligatoire('salarie.categoriePublic'), rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
  },
  niveauEntreeFormation: {
    ...addPre(preEstObligatoire('salarie.niveauEntreeFormation'), rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
  },
  diplomeOuTitreLePlusEleveObtenu: {
    ...addPre(
      preEstObligatoire('salarie.diplomeOuTitreLePlusEleveObtenu'),
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  intituleDiplome: {
    ...rgEstMaxLengthFactory(50),
  },
  adresse: {
    voie: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstVoieFactory(LevelRegleEnum.Metier),
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    },
    complement: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstVoieFactory(LevelRegleEnum.Metier),
    },
    codePostal: {
      ...rgEstMaxLengthFactory(10),
      ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    },
    commune: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstCommuneFactory(LevelRegleEnum.Metier),
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    },
  },
  telephone: {
    ...rgEstMaxLengthFactory(10),
    ...rgEstNumTelFactory(LevelRegleEnum.Metier),
  },
  email: {
    ...rgEstMaxLengthFactory(64),
    ...rgEstMailFactory(LevelRegleEnum.Metier),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  tuteur: {
    nom: {
      ...rgEstMaxLengthFactory(50),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
    },
    emploiOccupe: {
      ...rgEstMaxLengthFactory(100),
    },
    $model: {
      nomEtPrenom: {
        validateur: (tuteur) => {
          if (
            isNullUndefinedOrEmptyString(tuteur?.nom) &&
            isNullUndefinedOrEmptyString(tuteur?.prenom) &&
            isNullUndefinedOrEmptyString(tuteur?.emploiOccupe)
          )
            return
          return {
            nom: isNotNullUndefinedOrEmptyString(tuteur.nom),
            prenom: isNotNullUndefinedOrEmptyString(tuteur.prenom),
          }
        },
        message: SAISIE_OBLIGATOIRE,
        level: LevelRegleEnum.Metier,
      },
    },
  },
}

/* REGLES  Contrat  ------------------------------------------------------------ */
const reglesContratReservation: ReglesContrat = {
  nombreContrats: {
    ...rgEstNumeric,
    ...rgEstNumericMinFactory(0, LevelRegleEnum.Metier),
    ...rgEstNumericMaxFactory(21, LevelRegleEnum.Metier, 'La valeur doit être inférieure ou égale à 20'),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  referenceProjet: {
    ...rgEstMaxLengthFactory(15),
  },
  referenceInterneEntreprise: {
    ...rgEstMaxLengthFactory(30),
  },
  parcoursPTP: {
    ...addPre(preEstObligatoire('contrat.parcoursPTP'), rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
  },
}

const reglesContrat: ReglesContrat = {
  ...reglesContratReservation,
  /*
  codeDrf: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
   */
  cdiInterimaire: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre, 'Veuillez indiquer si le salarié est en CDI intérimaire.'),
  },
  dateConclusion: {
    ...addPre(preEstObligatoire('contrat.dateConclusion'), rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
    ...addPre(preEstPresent('contrat.dateConclusion'), {
      ...rgEstDate,
      checkDateConclusion: {
        pre: (_value, parent: ContratDTO) => parent.cdiInterimaire === true,
        validateur: (value) => isNotNullUndefinedOrEmptyString(value),
        message: 'Veuillez renseigner la date de conclusion du CDI intérimaire.',
        level: LevelRegleEnum.Transmettre,
      },
    }),
  },
  tempsPlein: {
    ...addPre(
      preEstObligatoire('contrat.tempsPlein'),
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre, 'Veuillez indiquer si le CDI intérimaire est à temps plein.'),
    ),
  },
  dureePeriodeEssaiJours: rgEstNumeric,
  dateDebutContrat: {
    ...addPre(
      preEstObligatoire('contrat.dateDebutContrat'),
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre, 'Veuillez renseigner la date de début du contrat.'),
    ),
    ...addPre(preEstPresent('contrat.dateDebutContrat'), {
      ...rgEstDate,
      'indentique-date-debut-formation': {
        pre: [preDateValide],
        validateur: (value, _parent, model) => {
          return model.formation?.modules?.some((m) => m.typeModule === 'Externe' && m.dateDebut === value)
        },
        message: 'La date de début du contrat doit correspondre à celle du début de la formation externe.',
        level: LevelRegleEnum.Transmettre,
      },
    }),
  },
  dateFinContrat: {
    ...addPre(
      preEstObligatoire('contrat.dateFinContrat'),
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre, 'Veuillez renseigner la date de fin de contrat.'),
    ),
    ...addPre(preEstPresent('contrat.dateFinContrat'), {
      ...rgEstDate,
      '4-semaines-min': {
        pre: [preDateValide, targetPre('contrat.dateDebutContrat', preDateValide)],
        validateur: (_, parent: ContratDTO) => {
          if (parent.dateDebutContrat! > parent.dateFinContrat!) return false
          const nbJours = getNbJour(new Date(parent.dateDebutContrat!), new Date(parent.dateFinContrat!))
          return nbJours >= 22
        },
        message: 'La durée du contrat doit être comprise entre 4 semaines et 6 mois.',
        level: LevelRegleEnum.Transmettre,
      },
      '6-mois-max': {
        pre: [preDateValide, targetPre('contrat.dateDebutContrat', preDateValide)],
        validateur: (_, parent) => {
          const dateDebut = new Date(parent.dateDebutContrat!)
          const dateMax = addMonth(dateDebut, 6)
          const dateFin = new Date(parent.dateFinContrat!)
          return dateFin <= dateMax
        },
        message: 'La durée du contrat doit être comprise entre 4 semaines et 6 mois.',
        level: LevelRegleEnum.Transmettre,
      },
      'inférieure-à-fin-formation-externe': {
        pre: [preDateValide],
        validateur: (value, _parent, modele) => {
          const { dateFin } = periodeFormationExternes(modele)
          if (!dateFin) return

          return dateFin <= value!
        },
        message: 'Les périodes de formation doivent être incluses dans les dates du contrat.',
        level: LevelRegleEnum.Transmettre,
      },
    }),
  },
  salaireHoraireFormation: {
    ...rgEstNumeric,
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    'min-smic': {
      pre: [preNotNullOrEmpty],
      validateur: (value, _parent, model) => {
        const { dateDebut } = periodeFormationExternes(model)
        if (isNullUndefinedOrEmptyString(dateDebut)) return
        const smicHorairebrute = model.employeur?.etablissement?.adresse?.codePostal?.startsWith(CODE_POSTAL_MAYOTTE)
          ? getSmicHoraireBrut('MAYOTTE', new Date(dateDebut!))
          : getSmicHoraireBrut('METROPOLE', new Date(dateDebut!))

        return value! >= smicHorairebrute
      },
      message: 'Le taux horaire est inférieur au SMIC à la date du début du contrat.',
      level: LevelRegleEnum.Metier,
    },
  },
  salaireHoraireMission: {
    ...addPre(preEstObligatoire('contrat.salaireHoraireMission'), rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
    ...addPre(preEstPresent('contrat.salaireHoraireMission'), {
      ...rgEstNumeric,
      'min-smic': {
        pre: [preNotNullOrEmpty, targetPre('mission.dateDebut', preDateValide)],
        validateur: (value, _parent, model) => {
          const smicHorairebrute = model.employeur?.etablissement?.adresse?.codePostal?.startsWith(CODE_POSTAL_MAYOTTE)
            ? getSmicHoraireBrut('MAYOTTE', new Date(model.mission!.dateDebut!))
            : getSmicHoraireBrut('METROPOLE', new Date(model.mission!.dateDebut!))
          console.log(smicHorairebrute)
          return value! >= smicHorairebrute
        },
        message: 'Le taux horaire est inférieur au SMIC à la date du début du contrat.',
        level: LevelRegleEnum.Metier,
      },
    }),
  },
  periodeInterruption: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.periodeInterruption?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  interruption: {
    dateDebutInterruption: {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ...rgEstDate,
      'sur-periode-contrat-cipi': {
        pre: [
          preEstCIPI,
          preDateValide,
          targetPre('contrat.dateDebutContrat', preDateValide),
          targetPre('contrat.dateFinContrat', preDateValide),
        ],
        validateur: (...[value, , model]) =>
          value! > model.contrat!.dateDebutContrat! && value! < model.contrat!.dateFinContrat!,
        message: 'Les périodes d’interruption doivent être incluses dans les dates du contrat.',
        level: LevelRegleEnum.Metier,
      },
      'sur-periode-formation-cdpi': {
        pre: [preEstCDPI, preDateValide],
        validateur: (value, _parent, modele) => {
          const periodeFormation = periodeFormationExternes(modele)
          return (
            (!periodeFormation.dateDebut || value! > periodeFormation.dateDebut) &&
            (!periodeFormation.dateFin || value! < periodeFormation.dateFin)
          )
        },
        message: 'Les périodes d’interruption doivent être incluses dans les dates de formations externes.',
        level: LevelRegleEnum.Metier,
      },
    },
    dateFinInterruption: {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ...rgEstDate,
      'sup-eguale-date-début-interruption': {
        pre: [preDateValide, (...[, parent]) => preDateValide(parent.dateDebutInterruption)],
        validateur: (_, parent) => parent.dateDebutInterruption! <= parent.dateFinInterruption!,
        message: "La date de fin d'interruption doit être supérieure à la date de début d'interruption",
        level: LevelRegleEnum.Metier,
      },
      'sur-periode-contrat-cipi': {
        pre: [
          preEstCIPI,
          preDateValide,
          targetPre('contrat.dateDebutContrat', preDateValide),
          targetPre('contrat.dateFinContrat', preDateValide),
        ],
        validateur: (...[value, , model]) =>
          value! > model.contrat!.dateDebutContrat! && value! < model.contrat!.dateFinContrat!,
        message: 'Les périodes d’interruption doivent être incluses dans les dates du contrat.',
        level: LevelRegleEnum.Metier,
      },
      'sur-periode-formation-cdpi': {
        pre: [preEstCDPI, preDateValide],
        validateur: (value, _parent, modele) => {
          const periodeFormation = periodeFormationExternes(modele)
          return (
            (!periodeFormation.dateDebut || value! > periodeFormation.dateDebut) &&
            (!periodeFormation.dateFin || value! < periodeFormation.dateFin)
          )
        },
        message: 'Les périodes d’interruption doivent être incluses dans les dates de formations externes.',
        level: LevelRegleEnum.Metier,
      },
    },
  },
  $model: {
    'duree-hebdomadaire-obligatoire': {
      pre: (...[, , context]) => context!.status.contrat?.dureeHebdomadaire?.$status === PropertyStatus.Required,
      validateur: (model) => {
        if (isNullUndefinedOrEmptyString(model.dureeHebdomadaire?.heure)) return { dureeHebdomadaire: false }
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    'duree-hebdomadaire-valide': {
      pre: [
        (...[, , context]) => context.status.contrat?.dureeHebdomadaire?.$status !== PropertyStatus.NotPresent,
        (model) => !isNullUndefinedOrEmptyString(model.dureeHebdomadaire?.heure),
      ],
      validateur: (model: ContratDTO) => {
        if (estDureeHebdomadaireDTO(model.dureeHebdomadaire)) return
        return { dureeHebdomadaire: false }
      },
      message: SAISIE_INVALIDE,
      level: LevelRegleEnum.Technique,
    },
    'duree-hebdomadaire-max-48h': {
      pre: [
        (...[, , context]) => context.status.contrat?.dureeHebdomadaire?.$status !== PropertyStatus.NotPresent,
        (model) => estDureeHebdomadaireDTO(model.dureeHebdomadaire),
      ],
      validateur: (model) => {
        if (model.dureeHebdomadaire!.heure! < 48) return
        if (model.dureeHebdomadaire!.heure! === 48 && model.dureeHebdomadaire!.minute === 0) return
        return { dureeHebdomadaire: false }
      },
      message: 'Maximum autorisé 48h',
      level: LevelRegleEnum.Transmettre,
    },
    'duree-hebdomadaire-min-1h': {
      pre: [
        (...[, , context]) => context.status.contrat?.dureeHebdomadaire?.$status !== PropertyStatus.NotPresent,
        (model) => estDureeHebdomadaireDTO(model.dureeHebdomadaire),
      ],
      validateur: (model) => {
        if (model.dureeHebdomadaire?.heure === 0) return { dureeHebdomadaire: false }
      },
      message: 'Minimum autorisé 1h',
      level: LevelRegleEnum.Transmettre,
    },
  },
}

/* REGLES  Formation  ------------------------------------------------------------ */
const reglesFormation: ReglesFormation = {
  dateDebutPrevisionnelle: {
    ...addPre<ReglesFormation>(preEstReservation, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ...rgEstDate,
      controleRange: {
        pre: [preDateValide],
        validateur: (value, _parent, _model) => {
          const dateDebutPrevisionnelle = new Date(value!)

          const today = getToday()

          const min = addWorkingDays(today, 1)
          const max = addDays(today, 28)
          return checkDateInRange(dateDebutPrevisionnelle, getRangeDate(min, max))
        },
        message: 'La date doit être entre 2 jours ouvrés et 4 semaines après la date du jour',
        level: LevelRegleEnum.Metier,
      },
    }),
  },
  dateFinPrevisionnelle: {
    ...addPre<ReglesFormation>(preEstReservation, {
      ...rgEstDate,
      'sup-date-debut-formation': {
        pre: [preDateValide, targetPre('formation.dateDebutPrevisionnelle', preDateValide)],
        validateur: (_value, parent) => {
          return parent.dateDebutPrevisionnelle! < parent.dateFinPrevisionnelle!
        },
        message: 'La date de fin doit être après la date de début.',
      },
      'duree-cipi': {
        pre: [preDateValide, targetPre('formation.dateDebutPrevisionnelle', preDateValide), preEstCIPI],
        validateur: (_value, _parent, model) => {
          const dateDebut = new Date(model.formation!.dateDebutPrevisionnelle!)
          const dateFin = new Date(model.formation!.dateFinPrevisionnelle!)

          const dureeJour = getNbJour(dateDebut, dateFin)
          const dateMax = addMonth(dateDebut, 6)

          return dureeJour >= 22 && dateFin <= dateMax
        },
        message: 'La durée du parcours doit être comprise entre 4 semaines et 6 mois.',
      },
      'duree-cdpi': {
        pre: [preDateValide, targetPre('formation.dateDebutPrevisionnelle', preDateValide), preEstCDPI],
        validateur: (_value, _parent, model) => {
          const dateDebut = new Date(model.formation!.dateDebutPrevisionnelle!)
          const dateFin = new Date(model.formation!.dateFinPrevisionnelle!)

          const dureeJour = getNbJour(dateDebut, dateFin)
          const dateMax = addMonth(dateDebut, 12)

          return dureeJour >= 22 && dateFin <= dateMax
        },
        message: 'La durée de la formation doit être comprise entre 4 semaines et 12 mois.',
      },
    }),
  },
  typeQualification: addPre(preEstReservation, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
  conventionCollective: addPre(preEstReservation, {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.conventionCollective?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    )
  }),
  niveau: addPre(preEstReservation, {
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.niveau?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstMaxLengthFactory(5),
  }),
  coefficient: addPre(preEstReservation, {
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.coefficient?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstMaxLengthFactory(10),
  }),
  nombreCCP: addPre(preEstReservation, {
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.nombreCCP?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstNumeric,
    ...rgEstNumericMinFactory(0),
  }),
  objectifGeneralFormation: addPre(preEstDossier, {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.objectifGeneralFormation?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  }),
  typeReferentielFormation: addPre(preEstReservation, {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.typeReferentielFormation?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  }),
  intitule: addPre(preEstReservation, {
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.intitule?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstMaxLengthFactory(50),
  }),
  diplomeOuTitreVise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.diplomeOuTitreVise?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  dureeTotale: {
    ...rgEstNumeric,
    'duree-min-cipi': {
      pre: [preEstCIPI, preNombreValide],
      validateur: (value) => value! >= 70,
      message: 'La durée de formation doit être supérieure ou égale à 70 heures.',
      level: LevelRegleEnum.Transmettre,
    },
    'duree-max-cipi': {
      pre: [preEstCIPI, preNombreValide],
      validateur: (value) => value! <= 245,
      message: 'La durée de formation doit être au maximum de 245 heures.',
      level: LevelRegleEnum.Transmettre,
    },
    'duree-min-cdpi': {
      pre: [preEstCDPI, preNombreValide],
      validateur: (value) => value! >= 140,
      message: 'La durée de formation doit être supérieure ou égale à 140 heures.',
      level: LevelRegleEnum.Transmettre,
    },
    'duree-max-cdpi': {
      pre: [preEstCDPI, preNombreValide],
      validateur: (value) => value! <= 525,
      message: 'La durée de formation doit être au maximum de 525 heures.',
      level: LevelRegleEnum.Transmettre,
    },
    'duree-differente-somme-externes-dossier': {
      pre: [preNombreValide],
      validateur: (value, parent, model) => {
        const nbModuleExterne = parent.modules?.length ?? 0
        if (nbModuleExterne === 0) return

        const sommeExternes = getNbHeureModulesExternes(model)
        if (!isNumber(sommeExternes)) return

        return value === sommeExternes
      },
      message:
        "La durée totale des modules n'est pas égale à la durée totale de formation. Veuillez corriger les durées saisies.",
      level: LevelRegleEnum.Transmettre,
    },
    'duree-differente-somme-externes-drf': {
      pre: [preNombreValide, preEstDossier],
      validateur: (value, _parent, model) => {
        const dureeTotale = model.drf?.formation?.dureeTotale ?? 0
        if (dureeTotale === 0) return

        return value! <= dureeTotale
      },
      message: 'La durée de formation ne peut être supérieure à celle demandée lors de la DRF.',
      level: LevelRegleEnum.Transmettre,
    },
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  dureeTotaleBilans: {
    ...rgEstNumeric,
    'duree-min-max': {
      pre: preNombreValide,
      validateur: (value, _parent) => value! >= 0 && value! <= 28,
      message: 'La durée des bilans doit être au maximum de 28 heures (14h par type de bilan).',
      level: LevelRegleEnum.Transmettre,
    },
    'duree-differente-somme-bilans-dossier': {
      pre: [preNombreValide],
      validateur: (value, parent, model) => {
        const nbModuleBilan = parent.modules?.length ?? 0
        if (nbModuleBilan === 0) return

        const sommeBilans = getNbHeureModulesAutres(model)
        if (!isNumber(sommeBilans)) return
        return value === sommeBilans
      },
      message:
        "La somme des durées des bilans n'est pas égale à la durée totale des bilans. Veuillez corriger les durées saisies.",
      level: LevelRegleEnum.Metier,
    },
    'duree-differente-somme-bilans-drf': {
      pre: [preNombreValide, preEstDossier],
      validateur: (value, _parent, model) => {
        const dureeTotale = model.drf?.formation?.dureeTotaleBilans ?? 0
        if (dureeTotale === 0) return

        return value! <= dureeTotale
      },
      message: 'La durée des bilans ne peut être supérieure à celle demandée lors de la DRF.',
      level: LevelRegleEnum.Metier,
    },
    ...rgEstObligatoireFactory(
      LevelRegleEnum.Transmettre,
      "Saisie obligatoire. Si aucun bilan n'est inclus dans la formation, veuillez saisir 0.",
    ),
  },

  modules: {
    formationRealiseeDansEntreprise: {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    },
    modalitePedagogique: {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    },
    dureeFoad: {
      ...addPre<ModuleDTO>(
        (_value, module, modele, context) => {
          const index = modele.formation?.modules?.indexOf(module)
          return context.status?.formation?.modules?.at(index!)?.dureeFoad?.$status === PropertyStatus.Required
        },
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...addPre<ModuleDTO>(
        (_value, module, modele, context) => {
          const index = modele.formation?.modules?.indexOf(module)
          return context.status?.formation?.modules?.at(index!)?.dureeFoad?.$status !== PropertyStatus.NotPresent
        },
        {
          ...rgEstNumeric,
          ...rgEstNumericMinFactory(0),
          controleMaximum: {
            pre: [preNotNullOrEmpty],
            validateur: (value, parent, _modele) => {
              if (!isNumber(parent.duree)) return
              if (parent.modalitePedagogique !== 'Mixte') return
              const duree = parent.duree
              const dureeFoad = value!
              return duree > dureeFoad
            },
            message: 'La durée de la formation à distance doit être inférieure à la durée de formation',
            level: LevelRegleEnum.Metier,
          },
        },
      ),
    },
    duree: {
      ...rgEstNumeric,
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ...addPre<ModuleDTO>((_value, parent) => parent.typeModule === 'Externe', {
        ...rgEstNumericMinFactory(0),
        'duree-max-cipi': {
          pre: [preEstCIPI, preNombreValide],
          validateur: (_value, _parent, model) => {
            const nbModuleExterne = model.formation?.modules?.filter((m) => m.typeModule! === 'Externe').length
            const nbHeuresExterne = (model.formation?.modules ?? [])
              .filter((m) => m.typeModule! === 'Externe')
              .reduce((acc, m) => acc + (m.duree ?? 0), 0)
            if (nbHeuresExterne > 245) {
              return nbModuleExterne === 1
                ? 'La durée de formation doit être au maximum de 245 heures.'
                : 'La durée totale des modules de formation doit être au maximum de 245 heures.'
            }

            return
          },

          level: LevelRegleEnum.Transmettre,
        },
        'duree-max-cdpi': {
          pre: [preEstCDPI, preNombreValide],
          validateur: (_value, _parent, model) => {
            const nbModuleExterne = model.formation?.modules?.filter((m) => m.typeModule! === 'Externe').length
            const nbHeuresExterne = (model.formation?.modules ?? [])
              .filter((m) => m.typeModule! === 'Externe')
              .reduce((acc, m) => acc + (m.duree ?? 0), 0)

            if (nbHeuresExterne > 525) {
              return nbModuleExterne === 1
                ? 'La durée de formation doit être au maximum de 525 heures.'
                : 'La durée totale des modules de formation doit être au maximum de 525 heures.'
            }

            return
          },
          level: LevelRegleEnum.Transmettre,
        },
      }),
      ...addPre<ModuleDTO>((_value, parent) => parent.typeModule !== 'Externe', {
        'duree-min-max': {
          pre: preNombreValide,
          validateur: (value, _parent) => value! > 0 && value! <= 14,
          message: 'La durée du bilan doit être au maximum de 14 heures.',
          level: LevelRegleEnum.Transmettre,
        },
      }),
    },
    horairesFormation: {
      ...rgEstMaxLengthFactory(50),
    },
    dateDebut: {
      ...rgEstDate,
      ...addPre(preEstDossier, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),

      'date-deb-sup-date-deb-formation': {
        pre: [preEstReservation, preDateValide, targetPre('formation.dateDebutPrevisionnelle', preDateValide)],
        validateur: (value, parent, model) => {
          if (!isDate(model.formation?.dateDebutPrevisionnelle)) return
          if (parent.typeModule !== 'Externe') return
          return value! >= model.formation?.dateDebutPrevisionnelle
        },
        message: 'La date doit être incluse dans les dates prévisionnelles de formation.',
        level: LevelRegleEnum.Metier,
      },
      'date-deb-sup-date-fin-formation': {
        pre: [preEstReservation, preDateValide, targetPre('formation.dateFinPrevisionnelle', preDateValide)],
        validateur: (value, parent, model) => {
          if (!isDate(model.formation?.dateFinPrevisionnelle)) return
          if (parent.typeModule !== 'Externe') return
          return value! <= model.formation?.dateFinPrevisionnelle
        },
        message: 'La date doit être incluse dans les dates prévisionnelles de formation.',
        level: LevelRegleEnum.Metier,
      },
      'cdpi-externe-date-sup-egale-4-semaines': {
        pre: [preEstCDPI, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'Externe') return
          const { dateDebut, dateFin } = periodeFormationExternes(model)
          if (!dateDebut || !dateFin) return
          if (dateDebut !== value) return

          const nbJours = getNbJour(new Date(dateDebut), new Date(dateFin))
          return nbJours >= 22
        },
        message: 'La durée de la formation doit être comprise entre 4 semaines et 12 mois',
        level: LevelRegleEnum.Metier,
      },
      'cipi-externe-date-sup-egale-4-semaines': {
        pre: [preEstCIPI, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'Externe') return
          const { dateDebut, dateFin } = periodeFormationExternes(model)
          if (!dateDebut || !dateFin) return
          if (dateDebut !== value) return

          const nbJours = getNbJour(new Date(dateDebut), new Date(dateFin))
          return nbJours >= 10
        },
        message: 'La durée de la formation est comprise entre 2 semaines (70h) et 6 mois.',
        level: LevelRegleEnum.Metier,
      },
      'cdpi-externe-date-inf-egale-12-mois': {
        pre: [preEstCDPI, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'Externe') return
          const { dateDebut, dateFin } = periodeFormationExternes(model)
          if (!dateDebut || !dateFin) return
          if (dateDebut !== value) return

          const dateMax = addMonth(new Date(dateDebut), 12)
          return new Date(dateFin) <= dateMax
        },
        message: 'La durée de la formation doit être comprise entre 4 semaines et 12 mois',
        level: LevelRegleEnum.Metier,
      },
      'cipi-evaluation-date-debut-supp-date-fin-contrat': {
        pre: [preEstCIPI, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'BilanEvaluation') return
          if (!value || !model.contrat?.dateFinContrat) return
          return value >= model.contrat.dateFinContrat
        },
        message: 'Ce bilan se situe en dernière étape et ne peut pas précéder la fin du contrat.',
        level: LevelRegleEnum.Metier,
      },
      'evaluation-sup-egale-date-fin-externe': {
        pre: [preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'BilanEvaluation') return
          const { dateFin } = periodeFormationExternes(model)
          if (!dateFin) return
          return value! >= dateFin
        },
        message: 'Ce bilan se situe en dernière étape du cycle et ne peut pas précéder la formation externe.',
        level: LevelRegleEnum.Metier,
      },
      'positionnement-sup-egale-date-debut-externe': {
        pre: [preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'BilanPositionnement') return
          const { dateDebut } = periodeFormationExternes(model)

          if (!dateDebut) return
          return value! <= dateDebut
        },
        message:
          'La date de début de ce bilan doit être antérieure ou égale à la date de début de la formation externe.',
        level: LevelRegleEnum.Metier,
      },
      'cxpi-externe-date-depot-sup-egale-48h': {
        pre: [preEstDossier, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'Externe') return

          const { dateDebut } = periodeFormationExternes(model)
          // const dateDepotDrf = new Date(model.dateDepotDrf!)
          // Cas dérogation au lancement
          if (isDerogationDepotDate(new Date(model.dateDepotDrf!))) return true

          const dateDepot = model.drf?.dateDepotDrf

          if (dateDebut !== value) return
          // Si dateDebut inférieure ou égale ) dateDepot
          if (dateDebut! <= dateDepot!) return false

          const nbJours = getNbJour(new Date(dateDepot!), new Date(dateDebut!))

          return nbJours >= 1 && nbJours <= 27
        },
        message:
          'La date de début de la formation doit se situer entre 2 jours et 28 jours calendaires après la date de la DRF.',
        level: LevelRegleEnum.Metier,
      },
    },
    dateFin: {
      ...rgEstDate,
      ...addPre(preEstDossier, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
      'date-fin-sup-date-debut': {
        pre: preDateValide,
        validateur: (value, parent) => {
          if (!isDate(parent.dateDebut)) return
          return value! >= parent.dateDebut
        },
        message: 'La date de fin doit être après la date de début.',
        level: LevelRegleEnum.Metier,
      },
      'date-fin-inf-date-fin-formation': {
        pre: [preEstReservation, preDateValide, targetPre('formation.dateFinPrevisionnelle', preDateValide)],
        validateur: (value, parent, model) => {
          if (!isDate(model.formation?.dateFinPrevisionnelle)) return
          if (parent.typeModule !== 'Externe') return
          return value! <= model.formation?.dateFinPrevisionnelle
        },
        message: 'La date doit être incluse dans les dates prévisionnelles de formation.',
        level: LevelRegleEnum.Metier,
      },
      'date-fin-sup-date-debut-formation': {
        pre: [preEstReservation, preDateValide, targetPre('formation.dateDebutPrevisionnelle', preDateValide)],
        validateur: (value, parent, model) => {
          if (!isDate(model.formation?.dateDebutPrevisionnelle)) return
          if (parent.typeModule !== 'Externe') return
          return value! >= model.formation?.dateDebutPrevisionnelle
        },
        message: 'La date doit être incluse dans les dates prévisionnelles de formation.',
        level: LevelRegleEnum.Metier,
      },

      'cdpi-externe-date-sup-egale-4-semaines': {
        pre: [preEstCDPI, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'Externe') return
          const { dateDebut, dateFin } = periodeFormationExternes(model)
          if (!dateDebut || !dateFin) return
          if (dateFin !== value) return

          const nbJours = getNbJour(new Date(dateDebut), new Date(dateFin))
          return nbJours >= 22
        },
        message: 'La durée de la formation doit être comprise entre 4 semaines et 12 mois',
        level: LevelRegleEnum.Metier,
      },
      'cipi-externe-date-sup-egale-4-semaines': {
        pre: [preEstCIPI, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'Externe') return
          const { dateDebut, dateFin } = periodeFormationExternes(model)
          if (!dateDebut || !dateFin) return
          if (dateFin !== value) return

          const nbJours = getNbJour(new Date(dateDebut), new Date(dateFin))
          return nbJours >= 10
        },
        message: 'La durée de la formation est comprise entre 2 semaines (70h) et 6 mois.',
        level: LevelRegleEnum.Metier,
      },

      'cdpi-externe-date-inf-egale-12-mois': {
        pre: [preEstCDPI, preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'Externe') return
          const { dateDebut, dateFin } = periodeFormationExternes(model)
          if (!dateDebut || !dateFin) return
          if (dateFin !== value) return

          const dateMax = addMonth(new Date(dateDebut), 12)
          return new Date(dateFin) <= dateMax
        },
        message: 'La durée de la formation doit être comprise entre 4 semaines et 12 mois',
        level: LevelRegleEnum.Metier,
      },
      'cipi-externe-date-inf-egale-fin-contrat': {
        pre: [preEstCIPI, preDateValide, targetPre('contrat.dateFinContrat', preDateValide)],
        validateur: (value, parent, modele) => {
          if (parent.typeModule !== 'Externe') return
          return value! <= modele.contrat!.dateFinContrat!
        },
        message: 'Les périodes de formation doivent être incluses dans les dates du contrat.',
        level: LevelRegleEnum.Metier,
      },
      'positionnement-sup-egale-date-debut-externe': {
        pre: [preDateValide],
        validateur: (value, parent, model) => {
          if (parent.typeModule !== 'BilanPositionnement') return
          const { dateDebut } = periodeFormationExternes(model)
          if (!dateDebut) return
          return value! <= dateDebut
        },
        message: 'La date de fin de ce bilan doit être antérieure ou égale à la date de début de la formation externe.',
        level: LevelRegleEnum.Metier,
      },
    },
    lieuFormation: {
      voie: {
        ...addPre<AdresseDTO>((_value, parent, modele, context) => {
          const index = modele.formation?.modules?.findIndex((module) => module.lieuFormation === parent) ?? -1
          return context.status.formation?.modules?.at(index)?.lieuFormation?.$status === PropertyStatus.Required
        }, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
        ...addPre<AdresseDTO>(
          (_value, parent, modele, context) => {
            const index = modele.formation?.modules?.findIndex((module) => module.lieuFormation === parent) ?? -1
            return context.status.formation?.modules?.at(index)?.lieuFormation?.$status !== PropertyStatus.NotPresent
          },
          {
            ...rgEstMaxLengthFactory(38),
            ...rgEstVoieFactory(LevelRegleEnum.Metier),
          },
        ),
      },
      complement: {
        ...addPre<AdresseDTO>(
          (_value, parent, modele, context) => {
            const index = modele.formation?.modules?.findIndex((module) => module.lieuFormation === parent) ?? -1
            return context.status.formation?.modules?.at(index)?.lieuFormation?.$status !== PropertyStatus.NotPresent
          },
          {
            ...rgEstMaxLengthFactory(38),
            ...rgEstVoieFactory(LevelRegleEnum.Metier),
          },
        ),
      },
      codePostal: {
        ...addPre<AdresseDTO>((_value, parent, modele, context) => {
          const index = modele.formation?.modules?.findIndex((module) => module.lieuFormation === parent) ?? -1
          return context.status.formation?.modules?.at(index)?.lieuFormation?.$status === PropertyStatus.Required
        }, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
        ...addPre<AdresseDTO>(
          (_value, parent, modele, context) => {
            const index = modele.formation?.modules?.findIndex((module) => module.lieuFormation === parent) ?? -1
            return context.status.formation?.modules?.at(index)?.lieuFormation?.$status !== PropertyStatus.NotPresent
          },
          {
            ...rgEstMaxLengthFactory(5),
            ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
          },
        ),
      },
      commune: {
        ...addPre<AdresseDTO>((_value, parent, modele, context) => {
          const index = modele.formation?.modules?.findIndex((module) => module.lieuFormation === parent) ?? -1
          return context.status.formation?.modules?.at(index)?.lieuFormation?.$status === PropertyStatus.Required
        }, rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
        ...addPre<AdresseDTO>(
          (_value, parent, modele, context) => {
            const index = modele.formation?.modules?.findIndex((module) => module.lieuFormation === parent) ?? -1
            return context.status.formation?.modules?.at(index)?.lieuFormation?.$status !== PropertyStatus.NotPresent
          },
          {
            ...rgEstMaxLengthFactory(50),
            ...rgEstCommuneFactory(LevelRegleEnum.Metier),
          },
        ),
      },
    },
    fraisAnnexesSalarie: {
      quantite: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'La quantité doit être supérieure à 0'),
        ...rgEstNumericMaxFactory(1000, LevelRegleEnum.Technique, 'La quantité doit être inférieure à 1000'),
      },
      montant: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
        ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      },
    },
    fraisPedagogiques: {
      montant: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
        ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      },
    },
    $model: {
      'etablissement-prestataire-obligatoire': {
        validateur: (model) => {
          if (isNotNullOrUndefined(model.etablissementPrestataire)) return
          return { etablissementPrestataire: SAISIE_OBLIGATOIRE }
        },
        level: LevelRegleEnum.Transmettre,
      },
      'etablissement-prestataire-actif': {
        pre: (model) => isNotNullUndefinedOrEmptyString(model.etablissementPrestataire?.etatAdministratif),
        validateur: (model) => {
          if (model.etablissementPrestataire!.etatAdministratif === 'A') return
          return {
            etablissementPrestataire:
              'Ce prestataire est inactif et ne peut pas être sélectionné. Veuillez sélectionner un établissement actif',
          }
        },
        level: LevelRegleEnum.Transmettre,
      },
      'lieu-formation-identique-obligatoire': {
        validateur: (model, globalModel, context) => {
          const index = globalModel.formation!.modules!.indexOf(model)
          const required =
            context.status.formation?.modules?.at(index)?.lieuFormationIdentiqueCfaPrincipal?.$status ===
            PropertyStatus.Required
          if (!required) return

          const hasAddress = Boolean(
            model.lieuFormation?.voie &&
            model.lieuFormation?.codePostal &&
            model.lieuFormation?.commune
          )
          if (isNullOrUndefined(model.lieuFormationIdentiqueCfaPrincipal) && !hasAddress)
            return { lieuFormationIdentiqueCfaPrincipal: SAISIE_OBLIGATOIRE }
        },
        level: LevelRegleEnum.Transmettre,
      },
    },
  },
  $model: {
    'rncp-obligatoire': {
      pre: (...[, model, context]) =>
        model.typeDemande === 'ReservationFonds' &&
        context!.status.formation?.rncp?.$status === PropertyStatus.Required,
      validateur: (model) => {
        if (isNotNullUndefinedOrEmptyString(model.rncp?.code)) return
        return { rncp: SAISIE_OBLIGATOIRE }
      },
      level: LevelRegleEnum.Transmettre,
    },
    'formacode-obligatoire': {
      pre: (...[, model, context]) =>
        model.typeDemande === 'ReservationFonds' &&
        context!.status.formation?.formacode?.$status === PropertyStatus.Required,
      validateur: (model) => {
        if (isNotNullUndefinedOrEmptyString(model.formacode?.code)) return
        return { formacode: SAISIE_OBLIGATOIRE }
      },
      level: LevelRegleEnum.Transmettre,
    },
    'nsf-obligatoire': {
      pre: (...[, model, context]) =>
        model.typeDemande === 'ReservationFonds' && context!.status.formation?.nsf?.$status === PropertyStatus.Required,
      validateur: (model) => {
        if (isNotNullUndefinedOrEmptyString(model.nsf?.code)) return
        return { nsf: SAISIE_OBLIGATOIRE }
      },
      level: LevelRegleEnum.Transmettre,
    },
    'module-externe-obligatoire': {
      validateur: (model, _globalModel, _context) => {
        const nbModuleExterne = model.modules?.filter((m) => m.typeModule === 'Externe').length ?? 0

        if (nbModuleExterne === 0) return { modules: 'Un module externe est obligatoire' }
      },
      level: LevelRegleEnum.Transmettre,
    },
  },
}

/* Financement */
const reglesFinancement: ReglesFinancement = {
  attestationResteChargeContribution: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.attestationResteChargeContribution?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  modePriseEnChargeReste: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.modePriseEnChargeReste?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
}

/* REGLES  Mission  ------------------------------------------------------------ */
const reglesMission: ReglesMission = {
  posteOccupe: {
    ...addPre((...[, , , context]) => context.status?.mission?.posteOccupe?.$status !== PropertyStatus.NotPresent, {
      ...rgEstMaxLengthFactory(200),
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  duree: {
    ...addPre((...[, , , context]) => context.status?.mission?.$status !== PropertyStatus.NotPresent, {
      ...rgEstNumeric,
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      'min-max': {
        pre: [preNotNullOrEmpty, preNombreValide],
        validateur: (value: number) => {
          if (value < 70) return 'La durée de mission doit être supérieure ou égale à 70 heures'
          if (value > 245) return 'La durée de mission doit être au maximum de 245 heures'

          return
        },
        level: LevelRegleEnum.Metier,
      },
      'max-cumul-formation': {
        pre: [preNotNullOrEmpty, preNombreValide],
        validateur: (value, _parent, model) => {
          const sommeDuréeFormation =
            model?.formation?.modules
              ?.filter((module) => module.typeModule === 'Externe')
              ?.map((module) => module.duree)
              ?.filter(isNumber)
              ?.reduce((acc, durée) => (acc += durée), 0) ?? 0
          return value! + sommeDuréeFormation <= 315
        },
        message: 'La durée totale de formation externe et de la mission ne doit pas dépasser 315h',
        level: LevelRegleEnum.Metier,
      },
    }),
  },
  dateDebut: {
    ...addPre((...[, , , context]) => context.status?.mission?.dateDebut?.$status !== PropertyStatus.NotPresent, {
      ...rgEstDate,
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      'date-sup-egale-date-debut-formation-externe': {
        pre: [preDateValide],
        validateur: (value, _parent, model) => {
          const { dateDebut } = periodeFormationExternes(model)
          if (!dateDebut) return
          return value! >= dateDebut
        },
        message: 'La mission doit être réalisée dans les 6 mois suivant le début de la formation.',
        level: LevelRegleEnum.Metier,
      },
      'date-inf-date-debut-formation-externe-plus-6-mois': {
        pre: [preDateValide],
        validateur: (value, _parent, model) => {
          const { dateDebut } = periodeFormationExternes(model)
          if (!dateDebut) return

          const dateMax = addMonth(new Date(dateDebut), 6)
          return new Date(value!) < dateMax
        },
        message: 'La mission doit être réalisée dans les 6 mois suivant le début de la formation.',
        level: LevelRegleEnum.Metier,
      },
    }),
  },
  dateFin: {
    ...addPre<MissionDTO>(
      (...[, , , context]) => context.status?.mission?.dateFin?.$status !== PropertyStatus.NotPresent,
      {
        ...rgEstDate,
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        'date-fin-sup-date-debut': {
          pre: [preDateValide, targetPre('mission.dateDebut', preDateValide)],
          validateur: (value, parent) => {
            return value! > parent.dateDebut!
          },
          message: 'La date de fin de mission doit être après la date de début',
          level: LevelRegleEnum.Metier,
        },
        'date-sup-egale-date-debut-formation-externe': {
          pre: [preDateValide],
          validateur: (value, _parent, model) => {
            const { dateDebut } = periodeFormationExternes(model)
            if (!dateDebut) return
            return value! >= dateDebut
          },
          message: 'La mission doit être réalisée dans les 6 mois suivant le début de la formation.',
          level: LevelRegleEnum.Metier,
        },
        'date-inf-date-debut-formation-externe-plus-6-mois': {
          pre: [preDateValide],
          validateur: (value, _parent, model) => {
            const { dateDebut } = periodeFormationExternes(model)
            if (!dateDebut) return

            const dateMax = addMonth(new Date(dateDebut), 6)
            return new Date(value!) < dateMax
          },
          message: 'La mission doit être réalisée dans les 6 mois suivant le début de la formation.',
          level: LevelRegleEnum.Metier,
        },
      },
    ),
  },
}

/* Regles model*/
export const reglesModel: ReglesCxpi = {
  validationNotice: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationSurHonneur: rgEstVraiFactory(LevelRegleEnum.Transmettre),
  attestationPiecesJustificatives: rgEstVraiFactory(LevelRegleEnum.Transmettre),
  acceptationCg: rgEstVraiFactory(LevelRegleEnum.Transmettre),
  lieuSignature: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstCommuneFactory(),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  dateSignature: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.dateSignature?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstDate,
    'sup-date-du-jour': {
      pre: [preDateValide],
      validateur: (value, _parent, _model) => {
        const today = getToday()
        const dateSignature = new Date(value!)
        return dateSignature <= today
      },
      message: 'La date de signature doit être antérieure ou égale à la date du jour',
      level: LevelRegleEnum.Metier,
    },
  },
  commentaire: rgEstMaxLengthFactory(256),
} as const

const reglesDrf: ReglesDrf = {
  dateFinValiditeDrf: {
    'sup-date-du-jour': {
      pre: [preDateValide, preEstDossier],
      validateur: (value, _parent, _model) => {
        const today = getToday()

        const dateFinValiditeDrf = new Date(value!)
        return dateFinValiditeDrf >= today
      },
      message: 'La DRF associée n’est pas valide',
      level: LevelRegleEnum.Transmettre,
    },
  },
  statut: {
    'drf-valide': {
      pre: [preEstDossier],
      validateur: (_value, _parent, model) => {
        const statutsAutorisés: StatutReservationFonds[] = ['Accorde', 'RelanceAvantCloture'] as const
        return statutsAutorisés.includes(model.drf!.statut!)
      },
      message: 'La DRF associée n’est pas valide',
      level: LevelRegleEnum.Transmettre,
    },
  },
  $model: {
    'documents-drf-complet': {
      level: LevelRegleEnum.Metier,
      validateur: (drf, model, context) => {
        const existDossier = drf.dossiersLies?.some((dossier) => !['Annule', 'Refuse'].includes(dossier.statut))
        if (existDossier) return
        const code = model.contrat?.codeDrf
        if (!code) return
        const documentsDossiers = getNaturesFichiersCxpi({ ...drf, code } as any, context.typeContext)
        const documentsExistants = drf.courriers?.flatMap((c) => c.nature) ?? []
        const documentsManquants = documentsDossiers.obligatoires
          .filter((natures) => !documentsExistants.includes(natures))
          .map((nature) => context.lov.NatureFichier.find((n) => n.code === nature)?.intitule)
        if (documentsManquants.length === 0) return
        return {
          courriers: `Le ou les documents suivants manquent sur la DRF : ${documentsManquants.join(', ')}`,
        }
      },
    },
  },
} as const
/* REGLES  ------------------------------------------------------------------------ */
export const reglesConfigurationFormation: ReglesCxpi = {
  employeur: reglesEmployeur,
  salarie: reglesSalarie,
  contrat: reglesContrat,
  formation: reglesFormation,
  mission: reglesMission,
  financement: reglesFinancement,
  drf: reglesDrf,
  $model: {
    controle16ansDateDebut: {
      pre: (model) => {
        return preDateValide(model.contrat?.dateDebutContrat) && preDateValide(model.salarie?.dateNaissance)
      },
      validateur: (model) => {
        const age = getAge(new Date(model.salarie!.dateNaissance!), new Date(model.contrat!.dateDebutContrat!))
        if (age < 16)
          return {
            'contrat.dateDebutContrat': false,
            'salarie.dateNaissance': false,
          }
      },
      message: 'Le salarié doit être âgé de 16 ans révolu à la date de démarrage du contrat',
      level: LevelRegleEnum.Metier,
    },
    controle16ansDateDebutFromation: {
      pre: (model) => {
        return (
          model.sousDispositif === 'CDPI' &&
          preDateValide(periodeFormationExternes(model).dateDebut) &&
          preDateValide(model.salarie?.dateNaissance)
        )
      },
      validateur: (model) => {
        const result: ModelValidateurResult<ContratCxpiDTO> = {}
        const dateFormation = periodeFormationExternes(model)
        const age = getAge(new Date(model.salarie!.dateNaissance!), new Date(dateFormation.dateDebut!))

        if (age < 16) {
          result['salarie.dateNaissance'] = false
          for (const [index, prop] of model.formation!.modules!.entries()) {
            if (isDate(prop.dateDebut)) {
              const ageModule = getAge(new Date(model.salarie!.dateNaissance!), new Date(prop.dateDebut!))
              if (prop.typeModule === 'Externe' && ageModule < 16) {
                result[`formation.modules.${index}.dateDebut`] = false
              }
            }
          }
        }
        return result
      },
      message: 'Le salarié doit être âgé de 16 ans révolu à la date de démarrage de la formation CDPI',
      level: LevelRegleEnum.Metier,
    },
    'fichiers-obligatoires': {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratCxpiDTO> = {}
        // Ne teste pas le nombre de fichier, car certains mettes plusieurs documents dans un scan
        for (const doc of context.naturesFichiers.obligatoires) {
          if (model.fichiers?.filter((x) => x.nature === doc).length === 0) {
            result[`fichiers`] = false
          }
        }
        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    'inscrit-France-Travail-en-cdi-interrimaire': {
      pre: (model) => isBoolean(model.salarie?.inscritFranceTravail) && isBoolean(model.contrat?.cdiInterimaire),
      validateur: (model) => {
        if (model.salarie?.inscritFranceTravail === true && model.contrat?.cdiInterimaire === true)
          return {
            'salarie.inscritFranceTravail': false,
            'contrat.cdiInterimaire': false,
          }
      },
      message:
        'Le salarié étant en CDI intérimaire, il ne peut pas être déclaré comme inscrit à France Travail. Veuillez corriger votre saisie.',
      level: LevelRegleEnum.Metier,
    },

    controleFinancementObligatoire: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratCxpiDTO> = {}

        if (context.status.financement?.$status === PropertyStatus.Required && isNullOrUndefined(model.financement))
          result['financement'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    'cipi-externe-date-sup-egale-debut-contrat': {
      validateur: (model, _globalModel, _context) => {
        const result: ModelValidateurResult<ContratCxpiDTO> = {}
        if (model.sousDispositif !== 'CIPI' || model.typeDemande !== 'Formation') return
        if (!isDate(model.contrat?.dateDebutContrat)) return

        const periodeFormation = periodeFormationExternes(model)
        if (periodeFormation.dateDebut! >= model.contrat!.dateDebutContrat!) return

        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (isDate(prop.dateDebut)) {
            if (prop.typeModule === 'Externe' && prop.dateDebut === periodeFormation.dateDebut) {
              result[`formation.modules.${index}.dateDebut`] = false
            }
          }
        }
        result['contrat.dateDebutContrat'] = false
        return result
      },
      message: 'Les périodes de formation doivent être incluses dans les dates du contrat.',
      level: LevelRegleEnum.Metier,
    },

    'cipi-externe-date-inf-egale-fin-contrat': {
      validateur: (model, _globalModel, _context) => {
        const result: ModelValidateurResult<ContratCxpiDTO> = {}
        if (model.sousDispositif !== 'CIPI' || model.typeDemande !== 'Formation') return
        if (!isDate(model.contrat?.dateFinContrat)) return

        const periodeFormation = periodeFormationExternes(model)
        if (periodeFormation.dateFin! <= model.contrat!.dateFinContrat!) return

        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (isDate(prop.dateFin)) {
            if (prop.typeModule === 'Externe' && prop.dateFin === periodeFormation.dateFin) {
              result[`formation.modules.${index}.dateFin`] = false
            }
          }
        }
        result['contrat.dateFinContrat'] = false
        return result
      },
      message: 'Les périodes de formation doivent être incluses dans les dates du contrat.',
      level: LevelRegleEnum.Metier,
    },
  },
  ...reglesModel,
} as const

export const reglesConfigurationReservation: ReglesCxpi = {
  employeur: reglesEmployeur,
  contrat: reglesContratReservation,
  formation: reglesFormation,
  $model: {
    'fichiers-obligatoires': {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratCxpiDTO> = {}
        // Ne teste pas le nombre de fichier, car certains mettes plusieurs documents dans un scan
        for (const doc of context.naturesFichiers.obligatoires) {
          if (model.fichiers?.filter((x) => x.nature === doc).length === 0) {
            result[`fichiers`] = false
          }
        }
        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
  },
  mission: reglesMission,
  ...reglesModel,
} as const
